import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Eye, EyeOff, Lock, ArrowLeft } from "lucide-react";
import { toast } from "react-hot-toast";
import { authAPI } from "../api/axiosClient";
import { AdminChangePasswordRequest } from "../api/types";

interface AdminPasswordResetFormProps {
  userId: string;
  passwordResetToken: string;
}

const AdminPasswordResetForm: React.FC<AdminPasswordResetFormProps> = ({
  userId,
  passwordResetToken,
}) => {
  console.log("AdminPasswordResetForm rendered with:", {
    userId,
    passwordResetToken,
  });

  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    newPassword: "",
    confirmPassword: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Password validation
  const passwordValidation = {
    isNumeric: /^\d+$/.test(formData.newPassword),
    isLength6: formData.newPassword.length === 6,
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.newPassword?.trim()) {
      newErrors.newPassword = "Yeni şifre alanı zorunludur";
    } else if (!passwordValidation.isNumeric) {
      newErrors.newPassword = "Şifre sadece rakam içermelidir";
    } else if (!passwordValidation.isLength6) {
      newErrors.newPassword = "Şifre 6 haneli olmalıdır";
    }

    if (!formData.confirmPassword?.trim()) {
      newErrors.confirmPassword = "Şifre tekrarı alanı zorunludur";
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = "Şifreler eşleşmiyor";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const request: AdminChangePasswordRequest = {
        userId,
        passwordResetToken,
        newPassword: formData.newPassword,
      };

      const response = await authAPI.adminChangePassword(request);

      if (response.success) {
        toast.success("Şifre başarıyla değiştirildi");
        // Şifre değiştirildikten sonra sayfayı yenile
        window.location.reload();
      } else {
        toast.error(response.message || "Şifre değiştirilirken hata oluştu");
      }
    } catch (error: any) {
      console.error("Error changing password:", error);
      toast.error("Şifre değiştirilirken hata oluştu");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Sol Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#9B51E0] relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-20 left-10 w-32 h-32 bg-white/10 rounded-full"></div>
          <div className="absolute top-40 right-20 w-48 h-48 bg-white/5 rounded-full"></div>
          <div className="absolute bottom-32 left-20 w-40 h-40 bg-white/10 rounded-full"></div>
          <div className="absolute bottom-20 right-10 w-24 h-24 bg-white/5 rounded-full"></div>
        </div>

        <div className="relative z-10 flex flex-col justify-center px-12 text-white">
          <div className="mb-8">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-6">
              <Lock className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl font-bold mb-4">Şifre Değiştir</h1>
            <p className="text-lg text-white/80">
              Hesabınız için yeni bir şifre belirleyin
            </p>
          </div>

          <div className="space-y-4 mt-8">
            <div className="flex items-center space-x-3 text-white/80">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <span>6 haneli numerik şifre</span>
            </div>
            <div className="flex items-center space-x-3 text-white/80">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <span>Güvenli ve hatırlanabilir</span>
            </div>
          </div>
        </div>
      </div>

      {/* Sağ Panel - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-gray-50">
        <div className="w-full max-w-md">
          <div className="lg:hidden text-center mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-[#9B51E0] rounded-2xl mb-4">
              <Lock className="w-8 h-8 text-white" />
            </div>
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900">Şifre Değiştir</h2>
            <p className="text-gray-500 mt-2">Yeni şifrenizi belirleyin</p>
          </div>

          <Card className="border-0 shadow-xl bg-white">
            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Yeni Şifre */}
                <div className="space-y-2">
                  <Label
                    htmlFor="newPassword"
                    className="text-sm font-medium text-gray-700"
                  >
                    Yeni Şifre <span className="text-red-500">*</span>
                  </Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      type={showPassword ? "text" : "password"}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      value={formData.newPassword}
                      onChange={(e) =>
                        handleInputChange("newPassword", e.target.value)
                      }
                      className={`h-11 pr-12 border-gray-200 focus:ring-2 focus:ring-[#9B51E0]/20 focus:border-[#9B51E0] ${errors.newPassword ? "border-red-500" : ""}`}
                      placeholder="••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>

                  {/* Password Strength Indicator */}
                  {formData.newPassword && (
                    <div className="space-y-2 mt-2">
                      <div className="flex space-x-2">
                        <div
                          className={`w-1/2 h-1.5 rounded-full transition-colors ${
                            passwordValidation.isNumeric
                              ? "bg-green-500"
                              : "bg-gray-200"
                          }`}
                        ></div>
                        <div
                          className={`w-1/2 h-1.5 rounded-full transition-colors ${
                            passwordValidation.isLength6
                              ? "bg-green-500"
                              : "bg-gray-200"
                          }`}
                        ></div>
                      </div>
                      <div className="text-xs space-y-1">
                        <div
                          className={`flex items-center space-x-2 transition-colors ${passwordValidation.isNumeric ? "text-green-600" : "text-gray-400"}`}
                        >
                          <div
                            className={`w-1.5 h-1.5 rounded-full ${passwordValidation.isNumeric ? "bg-green-500" : "bg-gray-300"}`}
                          ></div>
                          <span>Sadece rakam</span>
                        </div>
                        <div
                          className={`flex items-center space-x-2 transition-colors ${passwordValidation.isLength6 ? "text-green-600" : "text-gray-400"}`}
                        >
                          <div
                            className={`w-1.5 h-1.5 rounded-full ${passwordValidation.isLength6 ? "bg-green-500" : "bg-gray-300"}`}
                          ></div>
                          <span>6 haneli</span>
                        </div>
                      </div>
                    </div>
                  )}
                  {errors.newPassword && (
                    <p className="text-sm text-red-500 flex items-center mt-1">
                      <svg
                        className="w-4 h-4 mr-1"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                      {errors.newPassword}
                    </p>
                  )}
                </div>

                {/* Şifre Tekrarı */}
                <div className="space-y-2">
                  <Label
                    htmlFor="confirmPassword"
                    className="text-sm font-medium text-gray-700"
                  >
                    Şifre Tekrarı <span className="text-red-500">*</span>
                  </Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      value={formData.confirmPassword}
                      onChange={(e) =>
                        handleInputChange("confirmPassword", e.target.value)
                      }
                      className={`h-11 pr-12 border-gray-200 focus:ring-2 focus:ring-[#9B51E0]/20 focus:border-[#9B51E0] ${errors.confirmPassword ? "border-red-500" : ""}`}
                      placeholder="••••••"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="text-sm text-red-500 flex items-center mt-1">
                      <svg
                        className="w-4 h-4 mr-1"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                      {errors.confirmPassword}
                    </p>
                  )}
                </div>

                <div className="space-y-3 pt-2">
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full h-11 bg-[#9B51E0] hover:bg-[#8B41D0] text-white font-medium rounded-lg transition-all duration-200 shadow-lg shadow-[#9B51E0]/25"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center">
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Değiştiriliyor...
                      </span>
                    ) : (
                      "Şifreyi Değiştir"
                    )}
                  </Button>

                  <button
                    type="button"
                    onClick={() => navigate("/login")}
                    disabled={isSubmitting}
                    className="w-full flex items-center justify-center text-gray-500 hover:text-[#9B51E0] font-medium transition-colors py-2"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Giriş Sayfasına Dön
                  </button>
                </div>
              </form>
            </CardContent>
          </Card>

          <p className="text-center text-xs text-gray-400 mt-8">
            © 2026 EduPayz. Tüm hakları saklıdır.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminPasswordResetForm;
